import type { FC } from 'react'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@univdiam/ui/components/form'
import { Input } from '@univdiam/ui/components/input'
import { Button } from '@univdiam/ui/components/button'
import { zodResolver } from '@hookform/resolvers/zod'
import { POSTAL_CODE_MESSAGE, POSTAL_CODE_RE } from '@univdiam/ui/lib/postalCode'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import CountrySelect from '@univdiam/ui/customComponents/CountrySelect'
import ErrorComponent from '@univdiam/ui/customComponents/ErrorComponent'
import { LoaderCircle } from 'lucide-react'
import axios from 'axios'
import { useEffect } from 'react'
import { detectCountryCode } from '@univdiam/ui/lib/detectCountryCode'

const AddCustomerFormSchema = z.object({
    full_name: z.string().min(1, { message: 'Full Name is required.' }).max(100, 'Full Name cannot exceed 100 characters.'),
    email: z.string().email({ message: 'Please enter a valid email address.' }).min(1, { message: 'Email is required.' }).max(255, 'Email cannot exceed 255 characters.'),
    phoneNumberCountryCode: z.string().min(1, { message: 'Select phone country code.' }),
    phone_number: z.string().max(20, 'Phone number cannot exceed 20 characters.').optional(),

    business_name: z.string().min(1, { message: 'Business Name must be at least 2 characters.' }).max(100, 'Business Name cannot exceed 100 characters.'),

    businessPhoneNumberCountryCode: z.string().min(1, { message: 'Select business phone country code.' }),
    business_phone_number: z.string()
        .min(7, { message: 'Business phone must be at least 7 digits.' })
        .max(20, { message: 'Business phone must be at most 20 digits.' })
        .regex(/^\d+$/, { message: 'Business phone must contain digits only.' }),

    website: z.string().max(255, 'Website URL cannot exceed 255 characters.').optional(),

    address_line_1: z.string().min(1, { message: 'Address Line 1 must be at least 5 characters.' }).max(255, 'Address cannot exceed 255 characters.'),
    address_line_2: z.string().max(255, 'Address cannot exceed 255 characters.'),
    city: z.string().min(1, { message: 'City must be at least 2 characters.' }).max(100, 'City cannot exceed 100 characters.'),
    state: z.string().min(1, { message: 'State must be at least 2 characters.' }).max(100, 'State cannot exceed 100 characters.'),
    country: z.string().min(1, { message: 'Country must be at least 2 characters.' }).max(100, 'Country cannot exceed 100 characters.'),
    zip_code: z.string().trim()
        .min(1, { message: 'Postal code is required.' })
        .regex(POSTAL_CODE_RE, { message: POSTAL_CODE_MESSAGE }),
})

type AddCustomerFormValues = z.infer<typeof AddCustomerFormSchema>

type Props = {
    onSubmit: (values: AddCustomerFormValues) => void
    isLoading?: boolean
    isError?: boolean
    error?: Error | null
}

const AddCustomerForm: FC<Props> = ({ onSubmit, isLoading, isError, error }) => {
    const isAxiosError = axios.isAxiosError(error)

    const form = useForm<AddCustomerFormValues>({
        resolver: zodResolver(AddCustomerFormSchema),
        defaultValues: {
            full_name: '',
            email: '',
            phone_number: '',
            business_name: '',
            website: '',
            business_phone_number: '',
            address_line_1: '',
            address_line_2: '',
            city: '',
            state: '',
            country: '',
            zip_code: '',
            phoneNumberCountryCode: '+1',
            businessPhoneNumberCountryCode: '+1',
        },
    })

    const phoneNumber = form.watch('phone_number')
    const businessPhoneNumber = form.watch('business_phone_number')

    useEffect(() => {
        if (phoneNumber && phoneNumber.replace(/\D/g, '').length >= 2) {
            const detected = detectCountryCode(phoneNumber);
            if (detected) {
                form.setValue('phoneNumberCountryCode', detected, { shouldValidate: true });
            }
        }
    }, [phoneNumber, form])

    useEffect(() => {
        if (businessPhoneNumber && businessPhoneNumber.replace(/\D/g, '').length >= 2) {
            const detected = detectCountryCode(businessPhoneNumber);
            if (detected) {
                form.setValue('businessPhoneNumberCountryCode', detected, { shouldValidate: true });
            }
        }
    }, [businessPhoneNumber, form])

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col w-full space-y-8">
                <p className='text-caption2 fw-[500] text-[#202020] mb-[16px]'>Profile Details</p>
                <FormField
                    control={form.control}
                    name="full_name"
                    render={({ field }) => (
                        <FormItem className='mb-0'>
                            <FormLabel className='text-[11px] text-[#38404D] font-[500]'>Full Name <span className='text-[#16111]'>*</span></FormLabel>
                            <FormControl>
                                <Input {...field} maxLength={100} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                        <FormItem className='mt-[16px] mb-0'>
                            <FormLabel className='text-[11px] text-[#38404D] font-[500]'>Email <span className='text-[#16111]'>*</span></FormLabel>
                            <FormControl>
                                <Input {...field} type='email' maxLength={255} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name="phone_number"
                    render={({ field }) => (
                        <FormItem className='mt-[16px] mb-0'>
                            <FormLabel className='text-[11px] text-[#38404D] font-[500]'>Mobile Phone Number</FormLabel>
                            <FormControl>
                                <div className='relative w-full'>
                                    <FormField
                                        control={form.control}
                                        name="phoneNumberCountryCode"
                                        render={({ field }) => (
                                            <CountrySelect field={field} />
                                        )}
                                    />
                                    <Input {...field} className='pl-[100px]' maxLength={20} />
                                </div>
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <p className='text-caption2 fw-[500] text-[#202020] mb-[16px] mt-[24px]'>Business Details</p>
                <FormField
                    control={form.control}
                    name="business_name"
                    render={({ field }) => (
                        <FormItem className='mt-[16px] mb-0'>
                            <FormLabel className='text-[11px] text-[#38404D] font-[500]'>Business Name <span className='text-[#16111]'>*</span></FormLabel>
                            <FormControl>
                                <Input {...field} maxLength={100} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name="business_phone_number"
                    render={({ field }) => (
                        <FormItem className='mt-[16px] mb-0'>
                            <FormLabel className='text-[11px] text-[#38404D] font-[500]'>Phone Number <span className='text-[#16111]'>*</span></FormLabel>
                            <FormControl>
                                <div className='relative w-full'>
                                    <FormField
                                        control={form.control}
                                        name="businessPhoneNumberCountryCode"
                                        render={({ field }) => (
                                            <CountrySelect field={field} />
                                        )}
                                    />
                                    <Input {...field} className='pl-[100px]' maxLength={20} />
                                </div>
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name="website"
                    render={({ field }) => (
                        <FormItem className='mt-[16px] mb-0'>
                            <FormLabel className='text-[11px] text-[#38404D] font-[500]'>Website</FormLabel>
                            <FormControl>
                                <Input {...field} maxLength={255} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <p className='text-caption2 fw-[500] text-[#202020] mb-[16px] mt-[24px]'>Business Address</p>
                <FormField
                    control={form.control}
                    name="address_line_1"
                    render={({ field }) => (
                        <FormItem className='mt-[16px] mb-0'>
                            <FormLabel className='text-[11px] text-[#38404D] font-[500]'>Address Line 1 <span className='text-[#16111]'>*</span></FormLabel>
                            <FormControl>
                                <Input {...field} maxLength={255} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name="address_line_2"
                    render={({ field }) => (
                        <FormItem className='mt-[16px] mb-0'>
                            <FormLabel className='text-[11px] text-[#38404D] font-[500]'>Address Line 2</FormLabel>
                            <FormControl>
                                <Input {...field} maxLength={255} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <div className='flex items-center gap-[20px] my-0'>
                    <FormField
                        control={form.control}
                        name="city"
                        render={({ field }) => (
                            <FormItem className='w-full mt-[16px]'>
                                <FormLabel className='text-[11px] text-[#38404D] font-[500]'>City <span className='text-[#16111]'>*</span></FormLabel>
                                <FormControl>
                                    <Input {...field} className='w-full' maxLength={100} />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="state"
                        render={({ field }) => (
                            <FormItem className='w-full mt-[16px]'>
                                <FormLabel className='text-[11px] text-[#38404D] font-[500]'>State <span className='text-[#16111]'>*</span></FormLabel>
                                <FormControl>
                                    <Input {...field} className='w-full' maxLength={100} />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </div>
                <div className='flex items-center gap-[20px] my-0'>
                    <FormField
                        control={form.control}
                        name="country"
                        render={({ field }) => (
                            <FormItem className='w-full mt-[16px]'>
                                <FormLabel className='text-[11px] text-[#38404D] font-[500]'>Country <span className='text-[#16111]'>*</span></FormLabel>
                                <FormControl>
                                    <Input {...field} className='w-full' maxLength={100} />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="zip_code"
                        render={({ field }) => (
                            <FormItem className='mt-[16px]'>
                                <FormLabel className='text-[11px] text-[#38404D] font-[500]'>Zip Code <span className='text-[#16111]'>*</span></FormLabel>
                                <FormControl>
                                    <Input {...field} maxLength={10} />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </div>
                <div className='mt-[16px]'>
                    {isError && <ErrorComponent message={isAxiosError ? error?.response?.data?.error : ''} />}
                </div>
                <Button type="submit" className='w-full mt-[32px]' disabled={isLoading} isLoading={isLoading}>
    {'Add Customer'}
</Button>
            </form>
        </Form>
    )
}

export { AddCustomerForm, AddCustomerFormSchema }
export type { AddCustomerFormValues }
